
import { AppButton } from "./app-button.js";
import { AppTooltip } from "./app-tooltip.js";
import { IconCheck } from "./icons/check.js";
import { IconCopy } from "./icons/copy.js";

const styles = css`
  :host { display: block; min-height: 26px; }
  .actions { align-items: center; display: flex; gap: 2px; opacity: 0; transition: opacity 140ms ease; }
  :host(:hover) .actions, .actions:focus-within { opacity: 1; }
  app-button { color: var(--muted-foreground); }
  @media (hover: none) { .actions { opacity: 1; } }
  @media (prefers-reduced-motion: reduce) { .actions { transition: none; } }
`;

export class AppMessageActions extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppMessageActions)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["text","data-text","align","data-align"];

  #text = "";
  #align = "start";
  #copied;
  #textSet = false;
  #alignSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #attr0;
  #attr1;
  #attr2;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get text() {
    return this.#text;
  }

  set text(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#text, next)) return;
    this.#text = next;
    this.#textSet = true;
    this.#updateBindings('text');
  }

  get align() {
    return this.#align;
  }

  set align(value) {
    const raw = value ?? "start";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#align, next)) return;
    this.#align = next;
    this.#alignSet = true;
    this.#updateBindings('align');
  }

  #setCopied(value) {
    if (Object.is(this.#copied, value)) return;
    this.#copied = value;
    this.#updateBindings('copied');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-text')) this.text = this.dataset.text;
    if (this.hasAttribute('data-align')) this.align = this.dataset.align;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.text = props.text;
      this.align = props.align;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#textSet) this.text = this.dataset.text;
    if (!this.#alignSet) this.align = this.dataset.align;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'text':
      case 'data-text':
        this.text = newValue;
        break;
      case 'align':
      case 'data-align':
        this.align = newValue;
        break;
    }
  }

  #initState() {
    this.#copied = false;
  }

  #render() {
    const label = this.#copied ? "Copied" : "Copy message";
    return `<style>${this.#escText(styles)}</style><div class="actions" style="${this.#escAttr(`justify-content: ${(this.#align === "end") ? "flex-end" : "flex-start"}`)}" data-argon-bind="0"><app-tooltip data-text="${this.#attrEsc(label)}" data-argon-bind="1"><!--argon-text:0-->${""}<!--/argon-text:0--><app-button size="icon-xs" variant="ghost" aria-label="${this.#escAttr(label)}" data-argon="1082" data-argon-bind="2"><!--argon-text:1-->${""}${this.#copied ? `<icon-check>${""}</icon-check>` : `<icon-copy>${""}</icon-copy>`}<!--/argon-text:1--></app-button></app-tooltip></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#attr2 = root.querySelector('[data-argon-bind="2"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const label = (prop === undefined || prop === 'copied') ? (this.#copied ? "Copied" : "Copy message") : undefined;
    if (prop === undefined || prop === 'copied') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'copied') {
      const __argonNext = `${""}${this.#copied ? `<icon-check>${""}</icon-check>` : `<icon-copy>${""}</icon-copy>`}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'align') && this.#attr0) {
      const __argonNext = `${`justify-content: ${(this.#align === "end") ? "flex-end" : "flex-start"}`}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('style', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'copied') && this.#attr1) {
      const __argonNext = `${this.#attrRaw(label)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('data-text', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'copied') && this.#attr2) {
      const __argonNext = `${label}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr2.setAttribute('aria-label', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '1082').forEach(el => this.#listen(el, '1082', 'click', async () => {
    await navigator.clipboard.writeText(this.#text);
    this.#setCopied(true);
    setTimeout(() => { this.#setCopied(false); }, 1600);
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
